import { FAQ } from '../../types/chatbot';

export const faqData: FAQ[] = [
  // Account & Registration
  {
    id: 'create-account',
    question: 'How do I create an account?',
    answer: 'You can create an account by clicking the "Sign In" button in the top right corner and selecting "Create Account". You\'ll need to provide your email and create a password. We\'ll guide you through the rest of the process.',
    category: 'Account & Registration',
    links: [
      { text: 'Sign Up Now', url: '/signup' }
    ]
  },
  {
    id: 'account-security',
    question: 'How secure is my health information?',
    answer: 'Your health information is protected by industry-standard encryption and security measures. We comply with HIPAA regulations and use secure servers to store your data. Only authorized healthcare providers can access your medical information.',
    category: 'Account & Registration'
  },

  // Finding Healthcare
  {
    id: 'find-doctor',
    question: 'How do I find a doctor?',
    answer: 'You can find a doctor using our provider search feature. Filter by specialty, location, insurance acceptance, and more. You can also see ratings, availability, and book appointments directly through our platform.',
    category: 'Finding Healthcare',
    links: [
      { text: 'Search Providers', url: '/providers' }
    ]
  },
  {
    id: 'emergency-care',
    question: 'What should I do in a medical emergency?',
    answer: 'If you\'re experiencing a medical emergency, call emergency services immediately (911 in the US). For non-life-threatening emergencies, use our platform to find the nearest urgent care or emergency room.',
    category: 'Finding Healthcare',
    links: [
      { text: 'Find Emergency Care', url: '/emergency' }
    ],
    callToAction: {
      text: 'Call Emergency Services',
      number: '911'
    }
  },

  // Global Healthcare Access
  {
    id: 'healthcare-buddy',
    question: 'What is a Healthcare Buddy?',
    answer: 'A Healthcare Buddy is your local medical assistant who helps navigate the healthcare system in your destination country. They provide 24/7 support, assist with medical translations, coordinate care, and ensure you receive appropriate treatment.',
    category: 'Global Healthcare',
    links: [
      { text: 'Learn More', url: '/global' }
    ]
  },
  {
    id: 'global-coverage',
    question: 'Which countries are covered by your service?',
    answer: 'We currently operate in 25 countries across North America, Europe, Asia, and Oceania. Each location has vetted healthcare providers and local Healthcare Buddies to assist you. View our global coverage map for detailed information.',
    category: 'Global Healthcare',
    links: [
      { text: 'View Coverage Map', url: '/global' }
    ]
  },

  // Insurance & Payments
  {
    id: 'insurance-accepted',
    question: 'What insurance plans do you accept?',
    answer: 'We work with most major insurance providers including Blue Cross, Aetna, UnitedHealth, and many international insurance companies. You can verify your coverage when searching for providers or contact our support team for assistance.',
    category: 'Insurance & Payments',
    callToAction: {
      text: 'Verify Coverage',
      number: '(628) 333-5116'
    }
  },
  {
    id: 'payment-options',
    question: 'What payment methods are accepted?',
    answer: 'We accept all major credit cards, debit cards, HSA/FSA cards, and various digital payment methods. Some providers also offer payment plans. International payments are processed through secure global payment systems.',
    category: 'Insurance & Payments'
  },

  // Appointments & Scheduling
  {
    id: 'book-appointment',
    question: 'How do I schedule an appointment?',
    answer: 'You can schedule appointments directly through our platform. Search for a provider, select an available time slot, and book instantly. You\'ll receive confirmation via email and can manage your appointments through your account.',
    category: 'Appointments',
    links: [
      { text: 'Find & Book Provider', url: '/providers' }
    ]
  },
  {
    id: 'cancel-appointment',
    question: 'How do I cancel or reschedule an appointment?',
    answer: 'Log into your account, go to "My Appointments," and select the appointment you wish to modify. You can cancel or reschedule following our providers\' policies. Please note that some cancellations may incur fees if done with short notice.',
    category: 'Appointments'
  },

  // Symptom Checker
  {
    id: 'symptom-checker',
    question: 'How does the symptom checker work?',
    answer: 'Our AI-powered symptom checker analyzes your symptoms using advanced algorithms and medical databases. Enter your symptoms, answer a few questions, and receive personalized health insights and provider recommendations.',
    category: 'Features',
    links: [
      { text: 'Try Symptom Checker', url: '/#symptom-checker' }
    ]
  },
  {
    id: 'symptom-accuracy',
    question: 'How accurate is the symptom checker?',
    answer: 'Our symptom checker is designed for guidance only and shouldn\'t replace professional medical advice. It uses up-to-date medical knowledge and AI to provide insights with 85-90% accuracy for common conditions.',
    category: 'Features'
  },

  // Technical Support
  {
    id: 'technical-issues',
    question: 'I\'m having technical problems with the platform. What should I do?',
    answer: 'First, try refreshing your browser and clearing your cache. If issues persist, check our system status page or contact our technical support team for assistance.',
    category: 'Support',
    callToAction: {
      text: 'Contact Support',
      number: '(628) 333-5116'
    }
  },
  {
    id: 'forgot-password',
    question: 'I forgot my password. How do I reset it?',
    answer: 'Click the "Forgot Password" link on the login page. Enter your email address, and we\'ll send you instructions to reset your password. For security, the reset link expires after 24 hours.',
    category: 'Support',
    links: [
      { text: 'Reset Password', url: '/reset-password' }
    ]
  },

  // Privacy & Security
  {
    id: 'data-privacy',
    question: 'How do you protect my personal data?',
    answer: 'We employ industry-leading security measures including end-to-end encryption, secure servers, and regular security audits. Your data is stored in compliance with HIPAA and international data protection regulations.',
    category: 'Privacy & Security',
    links: [
      { text: 'Privacy Policy', url: '/privacy' }
    ]
  },
  {
    id: 'data-sharing',
    question: 'Who has access to my medical information?',
    answer: 'Only you and your authorized healthcare providers can access your medical information. We never share your data with third parties without your explicit consent, except when required by law.',
    category: 'Privacy & Security'
  },

  // Healthcare Buddy Program
  {
    id: 'buddy-qualifications',
    question: 'What qualifications do Healthcare Buddies have?',
    answer: 'Our Healthcare Buddies are medical professionals or certified healthcare navigators with extensive local experience. They undergo rigorous background checks and training in medical translation, cultural competency, and emergency response.',
    category: 'Healthcare Buddy'
  },
  {
    id: 'buddy-availability',
    question: 'How quickly can I connect with a Healthcare Buddy?',
    answer: 'Healthcare Buddies are available 24/7. In emergencies, you\'ll be connected immediately. For non-urgent matters, you\'ll typically be matched within 30 minutes. You can also pre-arrange a Buddy before traveling.',
    category: 'Healthcare Buddy',
    callToAction: {
      text: 'Connect with Buddy',
      number: '(628) 333-5116'
    }
  }
];

export const fallbackResponses = [
  "I'm not quite sure about that. Would you like me to connect you with a Healthcare Buddy who can help?",
  "I don't have enough information to answer that fully. Could you provide more details or rephrase your question?",
  "That's a bit outside my expertise. Let me connect you with someone who can help better.",
  "I want to make sure you get the best help possible. Would you like to speak with a healthcare professional?"
];

export const getRandomFallback = () => {
  return fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
};
